<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="search-view has-side-bars">
    <PageNotFound v-if="invalidSearchUrl" />
    <div v-else-if="dataSearch && thesaurusFiltersReady" class="results-wrapper">
      <div class="lateral-filters">
        <GrowUpMenu
          v-if="isMobile"
          :mobile-filters-open="mobileFiltersOpen"
          :clean-filters="cleanFilters"
          :title-menu="$t('filter_by')"
          @toggle-mobile-filter="toggleMobileFilter"
        >
          <template v-slot:menu-options>
            <LateralFilters
              :active-filter="activeFilter"
              :search-time-limits="searchTimeLimits"
              :thesaurus-filters="thesaurusFilters"
              @lateral-filter-pressed="lateralFilterChange"
              @toggle-filter="toggleFilter"
            />
          </template>
        </GrowUpMenu>
        <LateralFilters
          v-else
          :active-filter="activeFilter"
          :search-time-limits="searchTimeLimits"
          :thesaurus-filters="thesaurusFilters"
          @lateral-filter-pressed="lateralFilterChange"
          @toggle-filter="toggleFilter"
        />
      </div>
      <div class="main-container">
        <div class="search-input-container">
          <SearchInput @search-pressed="searchHandler" @toggle-data-loading="toggleDataLoading" />
          <div v-if="isMobile" class="button-filter-wrapper" @click="() => toggleMobileFilter()">
            <div class="icon-filter" />
          </div>
          <div v-if="dataLoading" class="icon-loader" />
        </div>
        <TypeFilter @lateral-filter-change="lateralFilterChange" />
        <div class="search-results-wrapper">
          <div class="search-results-container">
            <div class="search-results-text">{{ dataSearch.count }} {{ $t('results_for') }} <b>{{ searching }}</b></div>
            <FilterSort @toggle-data-loading="toggleDataLoading" />
          </div>
          <SearchSelectedFilters
            :thesaurus-filters="thesaurusFilters"
            @lateral-filter-pressed="lateralFilterChange"
            @toggle-filter="toggleFilter"
          />
        </div>
        <SearchList
          :data-search="dataSearch"
        />
        <PaginationSearch :data-search="dataSearch" :items-for-page="itemsForPage" @toggle-data-loading="toggleDataLoading" />
      </div>
    </div>
    <Loader v-else :text="$t('loading')" />
  </div>
</template>

<script>
import GrowUpMenu from '../components/mobile/GrowUpMenu.vue'
import FilterSort from '../components/search/FilterSort.vue'
import LateralFilters from '../components/search/LateralFilters.vue'
import PaginationSearch from '../components/search/PaginationSearch.vue'
import PageNotFound from '../components/PageNotFound.vue'
import SearchSelectedFilters from '../components/search/SearchSelectedFilters.vue'
import SearchInput from '../components/SearchInput.vue'
import SearchList from '../components/search/SearchList.vue'
import Loader from '../components/Loader.vue'
import TypeFilter from '../components/search/TypeFilter.vue'
import dayjs from 'dayjs'
import { getThesaurus, querySearch } from '@/services/api'
import { resolveThesaurusLabel } from '@/utils/thesaurusUtils'
import device from '@/utils/device'

const TRAILING_PUNCTUATION = /[^\p{L}\p{N}/]+$/u

function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value)
  } catch (error) {
    return value
  }
}

function cleanTrailingPunctuation(value) {
  if (typeof value !== 'string') return value
  return safeDecodeURIComponent(value).replace(TRAILING_PUNCTUATION, '')
}

/**
 * The Search page of Portal with params and query params of search in the url
 * @displayName Search
 * @example [none]
 */
export default {
  components: {
    GrowUpMenu,
    FilterSort,
    LateralFilters,
    PaginationSearch,
    PageNotFound,
    SearchSelectedFilters,
    SearchInput,
    SearchList,
    Loader,
    TypeFilter,
  },
  props: {
    /**
     * params of searching (words)
     */
    params: {
      type: String,
    },
  },
  data() {
    return {
      activeFilter: '',
      searching: '',
      itemsForPage: 10,
      dataLoading: false,
      mobileFiltersOpen: false,
    }
  },
  asyncComputed: {
    thesaurusFilters() {
      return getThesaurus(this.$store.state.i18n.lang)
    },
    dataSearch() {
      if (this.invalidSearchUrl) return null
      this.searching = this.params
      const territoryForApi = this.$route.query.territory || undefined

      return querySearch(
        (this.$route.query.page ? parseInt(this.$route.query.page) - 1 : 0) * this.itemsForPage,
        this.itemsForPage,
        this.$store.state.i18n.lang,
        this.params ? this.params : '',
        this.$route.query.theme,
        this.$route.query.subtheme,
        this.$route.query.detail,
        this.$route.query.district,
        this.$route.query.source,
        this.sortByFilter,
        this.$route.query.publishDateRange,
        territoryForApi,
        this.$route.query.dimensions,

      )
    },
  },
  computed: {
    invalidSearchUrl() {
      return Object.values(this.$route.query || {}).some(value => {
        if (Array.isArray(value)) {
          return value.some(entry => cleanTrailingPunctuation(entry) !== entry)
        }
        return cleanTrailingPunctuation(value) !== value
      })
    },
    getDevice() {
      return device
    },
    hasThesaurusSelection() {
      return !!(this.$route.query.theme || this.$route.query.subtheme || this.$route.query.detail)
    },
    thesaurusFiltersReady() {
      return !this.hasThesaurusSelection || !!this.thesaurusFilters
    },
    searchOptions() {
      return this.dataSearch
    },
    searchTimeLimits() {
      if (this.dataSearch.publishDateInit && this.dataSearch.publishDateEnd) {
        let timeLimits = {
          min: dayjs(this.dataSearch.publishDateInit.split('T')[0] + 'T00:00:00Z'),
          max: dayjs(this.dataSearch.publishDateEnd.split('T')[0] + 'T00:00:00Z'),
        }
        return timeLimits
      } else return null
    },
    queryLateralFilters() {
      let filterObj = []
      let arrayWords = []
      if (this.$route.query.theme) {
        arrayWords = this.$route.query.theme.split(',')
        arrayWords.forEach(el =>
          filterObj.push({
            value: el,
            type: 'theme',
          })
        )
      }
      if (this.$route.query.subtheme) {
        arrayWords = this.$route.query.subtheme.split(',')
        arrayWords.forEach(el =>
          filterObj.push({
            value: el,
            type: 'subtheme',
          })
        )
      }
      if (this.$route.query.detail) {
        arrayWords = this.$route.query.detail.split(',')
        arrayWords.forEach(el =>
          filterObj.push({
            value: el,
            type: 'detail',
          })
        )
      }
      return filterObj
    },
    sortByFilter() {
      return this.$route.query.sortBy ? this.$route.query.sortBy : 'relevance'
    },
  },
  watch: {
    // whenever search was loaded, this function will run
    searchOptions() {
      this.toggleDataLoading(false)
    },
  },
  methods: {
    /**
     * Clean all selected filters
     * @public This is a public method
     */
    cleanFilters() {
      this.$router.push({
        name: 'Search',
        params: { params: this.$route.params.params },
        query: {},
      })
    },
    /**
     * Toggle mobile filter
     * @public This is a public method
     * @param {Boolean} val value that wants to set
     */
    toggleMobileFilter(val) {
      this.mobileFiltersOpen = val ? val : !this.mobileFiltersOpen
    },
    /**
     * Toggle one of the lateral filters avaliable
     * @public This is a public method
     * @param {String} filter Id of selected filter to toggle
     */
    toggleFilter(filter) {
      this.activeFilter = this.activeFilter === filter ? '' : filter
    },
    /**
     * Toggle loading to on/off
     * @public This is a public method
     * @param {String} val not required value that wants to set
     */
    toggleDataLoading: function (val) {
      this.dataLoading = val !== undefined ? val : !this.dataLoading
    },
    /**
     * Detect when the user search a new value and update the url
     * @public This is a public method
     * @param {String} value search string value
     */
    searchHandler: function (value) {
      let defaultParams = {}
      this.activeFilter = ''
      if (value !== '') defaultParams = { params: value }
      if (this.$route.params.params !== value) {
        if (value === '' && !this.$route.params.params) this.toggleDataLoading()
        let queryPartial = {}
        if (this.$route.query) {
          queryPartial = { ...this.$route.query }
          delete queryPartial.page
          delete queryPartial.publishDateRange
        }
        this.$router.push({ name: 'Search', params: defaultParams, query: queryPartial })
      } else this.toggleDataLoading()
    },
    /**
     * Update query params with current lateral filter information
     * @param {String} filtVal Value of selected filter
     * @param {String} filtType Type of filter to change (theme, subtheme, detail, source, district)
     * @param {Boolean} recursive to know if function has recursive calls
     * @public This is a public method
     */
    lateralFilterChange(filtVal, filtType, multiselect, recursive) {
      if (!recursive) this.toggleDataLoading()
      let arrayFilters = []
      if (this.$route.query[filtType]) {
        arrayFilters = this.$route.query[filtType].split(',')
        if (typeof arrayFilters === String) arrayFilters = [arrayFilters]
        if (filtType === 'theme') {
          this.queryLateralFilters.forEach(el => {
            if (el.type === 'subtheme')
              this.lateralFilterChange(el.value, el.type, multiselect, true)
          })
        }
        if (filtType === 'subtheme') {
          this.queryLateralFilters.forEach(el => {
            if (el.type === 'detail') this.lateralFilterChange(el.value, el.type, multiselect, true)
          })
        }
        if (arrayFilters.find(el => el === filtVal)) {
          arrayFilters.splice(arrayFilters.indexOf(filtVal), 1)
        } else {
          if (!multiselect) arrayFilters = [filtVal]
          else {
            if (filtVal === '') arrayFilters = []
            else arrayFilters.push(filtVal)
          }
        }
      } else arrayFilters.push(filtVal)
      arrayFilters = arrayFilters.join(',')
      if (arrayFilters !== '') {
        let queryUpdate = { ...this.$route.query }
        //reset pagination on filter
        if (queryUpdate.page) delete queryUpdate.page
        if (queryUpdate.publishDateRange) delete queryUpdate.publishDateRange
        if (filtType === 'theme') {
          delete queryUpdate.subtheme
          delete queryUpdate.subthemeLabel
          delete queryUpdate.detail
          delete queryUpdate.detailLabel
        } else if (filtType === 'subtheme') {
          delete queryUpdate.detail
          delete queryUpdate.detailLabel
        }
        if (filtType !== 'publishDateRange' || arrayFilters.indexOf('Invalid Date') === -1) {
          queryUpdate[filtType] = arrayFilters
          if (filtType === 'theme' || filtType === 'subtheme' || filtType === 'detail') {
            queryUpdate[filtType + 'Label'] = arrayFilters
              .split(',')
              .map(value => resolveThesaurusLabel(this.thesaurusFilters && this.thesaurusFilters.theme, value))
              .join(',')
          }
          if (filtType === 'territory' && filtVal === 'codi postal') {
            // Keep territory (including "codi postal") for UI state and backend flag
            queryUpdate.territory = arrayFilters

            // Ensure dimensions doesn't include "Codi postal" (avoid AND restriction)
            const currentDimensions = queryUpdate.dimensions ? queryUpdate.dimensions.split(',') : []
            const nextDimensions = currentDimensions.filter(d => d !== 'Codi postal')
            if (nextDimensions.length) queryUpdate.dimensions = nextDimensions.join(',')
            else delete queryUpdate.dimensions
          } else {
            queryUpdate[filtType] = arrayFilters
          }
          this.$router.push({
            name: 'Search',
            params: { params: this.$route.params.params },
            query: {
              ...queryUpdate,
            },
          })
        }
      } else {
        let queryUpdate = { ...this.$route.query }
        delete queryUpdate[filtType]
        if (filtType === 'theme' || filtType === 'subtheme' || filtType === 'detail') {
          delete queryUpdate[filtType + 'Label']
        }
        this.$router.push({
          name: 'Search',
          params: { params: this.$route.params.params },
          query: {
            ...queryUpdate,
          },
        })
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.search-view
  position: relative
  flex: 1 1 0%
  // overflow: hidden
  padding-top: vw(40px)
  padding-bottom: vw(130px)

  .mobile &
    padding-bottom: vw(40px)

.results-wrapper
  display: flex
  min-height: "calc(100vh - %s - %s - %s)" % ($header-height $subheader-height $footer-height)
  gap: vw(60px)

.search-input-container
  display: flex
  align-items: center
  margin-bottom: vw(30px)

  .mobile &
    margin-bottom: vw(20px)
    gap: vw(15px)

  .button-filter-wrapper
    padding: vw(10px)
    border: 1px solid black
    border-radius: 50%

    .icon-filter
      m-icon("filter", 20, $black)

.lateral-filters
  flex: 0 0 vw(300px)
  width: vw(300px)

  .mobile &
    position: fixed
    bottom: 0
    left: 0
    z-index: 8
    padding-top: 0
    width: 100%
    transition: transform 0.25s ease-in-out

  &.menu-grow
    transform: translateY(0)

.main-container
  display: flex
  flex: 1 1 0%
  flex-direction: column
  max-width: 100%

  >>> .search-input-wrapper
    width: 50%

    .mobile &
      width: 100%

.search-results-wrapper
  margin-bottom: vw(30px)

  .mobile &
    margin-bottom: vw(10px)

.search-results-container
  display: flex
  justify-content: space-between
  align-items: center
  margin-bottom: vw(20px)

  .mobile &
    margin-bottom: vw(10px)

.search-results-text
  m-font-size(14, 20)
  color: #000

.icon-loader
  m-icon("loader", 20)
  margin-left: vw(20px)
  animation: rotate 1s infinite linear

@keyframes rotate
  from
    transform: rotate(0)

  to
    transform: rotate(360deg)
</style>
